import { type BaseComponentProps } from "../../types/puck";
import { cn } from "../../lib/utils";
import { MapPin, ArrowRight } from "lucide-react";
import { SmartLink } from "../smart-link";
import { ServiceAreaMap } from "./service-area-map";
import type { JobsiteServiceAreaBoundary } from "../../types/runtime-config";

export interface ServiceAreaItem {
  name: string;
  /** Town page URL segment. Absent on hand-typed areas and sites built before it existed. */
  slug?: string;
  displayName: string;
  url?: string;
  description?: string;
  latitude?: number | null;
  longitude?: number | null;
  boundary?: JobsiteServiceAreaBoundary | null;
  surroundingAreas?: boolean;
}

/**
 * Area name to URL slug. Mirrors the API's `slugify()` for the ASCII city
 * names that reach here (`ServiceArea.cityAscii`), which is what
 * `/locations/<slug>` paths are built from.
 */
export function slugifyAreaName(name: string): string {
  return name
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export interface ServiceAreasProps extends BaseComponentProps {
  heading: string;
  subheading?: string;
  areas: ServiceAreaItem[];
  layout: "grid" | "list" | "compact";
  showLinks: boolean;
  showDescriptions: boolean;
  showMap?: boolean;
  googleMapsApiKey?: string;
  backgroundColor?: string;
  /** Base URL for generating area links (e.g., '/locations/' or '/services/drain-cleaning/') */
  baseUrl?: string;
}

export const ServiceAreas: React.FC<ServiceAreasProps> = ({
  heading,
  subheading,
  areas,
  layout = "grid",
  showLinks = true,
  showDescriptions = false,
  showMap = true,
  googleMapsApiKey,
  backgroundColor,
  baseUrl,
  id,
  className,
}) => {
  const surfaceRadiusStyle = { borderRadius: "var(--jobsite-radius)" } as const;
  const getDisplayName = (area: ServiceAreaItem): string =>
    area.surroundingAreas ? `${area.displayName} and surrounding areas` : area.displayName;
  const getAreaUrl = (area: ServiceAreaItem): string | undefined => {
    if (area.url) return area.url;
    if (baseUrl) return `${baseUrl}${area.slug ?? slugifyAreaName(area.name)}/`;
    return undefined;
  };

  const renderGridLayout = () => (
    <div className="jobsite:grid jobsite:grid-cols-2 jobsite:md:grid-cols-3 jobsite:lg:grid-cols-4 jobsite:gap-4">
      {areas.map((area, idx) => {
        const url = getAreaUrl(area);
        const content = (
          <div
            className={cn(
              "jobsite:flex jobsite:items-center jobsite:gap-3 jobsite:p-4",
              "jobsite:bg-background jobsite:border jobsite:border-border/70",
              "jobsite:transition-all jobsite:duration-200",
              showLinks &&
                url &&
                "jobsite:hover:border-primary/40 jobsite:hover:shadow-sm jobsite:cursor-pointer",
            )}
            style={surfaceRadiusStyle}
          >
            <MapPin className="jobsite:w-5 jobsite:h-5 jobsite:text-primary jobsite:shrink-0" />
            <div className="jobsite:flex-1 jobsite:min-w-0">
              <span className="jobsite:font-semibold jobsite:text-card-foreground jobsite:block jobsite:truncate">
                {getDisplayName(area)}
              </span>
              {showDescriptions && area.description && (
                <span className="jobsite:text-sm jobsite:text-muted-foreground jobsite:line-clamp-1">
                  {area.description}
                </span>
              )}
            </div>
            {showLinks && url && (
              <ArrowRight className="jobsite:w-4 jobsite:h-4 jobsite:text-muted-foreground" />
            )}
          </div>
        );

        if (showLinks && url) {
          return (
            <SmartLink key={idx} to={url} className="jobsite:no-underline">
              {content}
            </SmartLink>
          );
        }
        return <div key={idx}>{content}</div>;
      })}
    </div>
  );

  const renderListLayout = () => (
    <div className="jobsite:space-y-3 jobsite:max-w-2xl jobsite:mx-auto">
      {areas.map((area, idx) => {
        const url = getAreaUrl(area);
        const content = (
          <div
            className={cn(
              "jobsite:flex jobsite:items-center jobsite:justify-between jobsite:gap-4 jobsite:p-4",
              "jobsite:bg-background jobsite:border jobsite:border-border/70",
              "jobsite:transition-all jobsite:duration-200",
              showLinks &&
                url &&
                "jobsite:hover:border-primary/40 jobsite:hover:shadow-sm jobsite:cursor-pointer",
            )}
            style={surfaceRadiusStyle}
          >
            <div className="jobsite:flex jobsite:items-center jobsite:gap-3">
              <MapPin className="jobsite:w-5 jobsite:h-5 jobsite:text-primary jobsite:shrink-0" />
              <div>
                <span className="jobsite:font-semibold jobsite:text-card-foreground jobsite:block">
                  {getDisplayName(area)}
                </span>
                {showDescriptions && area.description && (
                  <span className="jobsite:text-sm jobsite:text-muted-foreground">
                    {area.description}
                  </span>
                )}
              </div>
            </div>
            {showLinks && url && (
              <ArrowRight className="jobsite:w-5 jobsite:h-5 jobsite:text-primary" />
            )}
          </div>
        );

        if (showLinks && url) {
          return (
            <SmartLink key={idx} to={url} className="jobsite:no-underline jobsite:block">
              {content}
            </SmartLink>
          );
        }
        return <div key={idx}>{content}</div>;
      })}
    </div>
  );

  const renderCompactLayout = () => (
    <div className="jobsite:flex jobsite:flex-wrap jobsite:gap-2 jobsite:justify-center">
      {areas.map((area, idx) => {
        const url = getAreaUrl(area);
        const content = (
          <span
            className={cn(
              "jobsite:inline-flex jobsite:items-center jobsite:gap-2 jobsite:px-4 jobsite:py-2",
              "jobsite:bg-muted jobsite:text-sm jobsite:font-medium",
              "jobsite:text-muted-foreground jobsite:transition-colors",
              showLinks &&
                url &&
                "jobsite:hover:bg-primary jobsite:hover:text-primary-foreground jobsite:cursor-pointer",
            )}
            style={surfaceRadiusStyle}
          >
            <MapPin className="jobsite:w-4 jobsite:h-4" />
            {getDisplayName(area)}
          </span>
        );

        if (showLinks && url) {
          return (
            <SmartLink key={idx} to={url} className="jobsite:no-underline">
              {content}
            </SmartLink>
          );
        }
        return <span key={idx}>{content}</span>;
      })}
    </div>
  );

  const hasMapData = showMap && googleMapsApiKey && areas.length > 0;

  return (
    <section
      id={id}
      className={cn(
        "jobsite:py-16 jobsite:md:py-24",
        backgroundColor ? "" : "jobsite:bg-background",
        className,
      )}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      <div className="jobsite:container jobsite:mx-auto jobsite:px-4">
        {/* Header */}
        <div className="jobsite:text-center jobsite:mb-12 jobsite:max-w-3xl jobsite:mx-auto">
          <h2 className="jobsite:text-3xl jobsite:sm:text-4xl jobsite:mb-4 jobsite:font-heading jobsite:tracking-tight jobsite:text-balance jobsite:text-foreground">
            {heading}
          </h2>
          {subheading && (
            <p className="jobsite:text-lg jobsite:text-pretty jobsite:text-muted-foreground">{subheading}</p>
          )}
        </div>

        {/* Interactive Google Map with boundary polygons */}
        {hasMapData && (
          <div
            className="jobsite:mb-10 jobsite:overflow-hidden jobsite:shadow-md"
            style={{ borderRadius: "var(--jobsite-radius)" }}
          >
            <ServiceAreaMap apiKey={googleMapsApiKey!} areas={areas} height={400} />
          </div>
        )}

        {/* Service Areas */}
        {layout === "grid" && renderGridLayout()}
        {layout === "list" && renderListLayout()}
        {layout === "compact" && renderCompactLayout()}
      </div>
    </section>
  );
};
